/* Kingdom R7: the contact card. Any element with [data-contact-card] opens it.
   Email is a plain link; copy uses the clipboard; save builds a vCard (with the isotype
   as the contact photo when it can be fetched) and hands it to the device's contacts. */
(() => {
  const cc = document.getElementById('kw-contact-card');
  if (!cc) return;
  const card = cc.querySelector('.kw-cc-card');
  const status = cc.querySelector('.kw-cc-status');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let trigger = null;
  let closeTimer = null;

  const focusables = () => [...card.querySelectorAll('a[href],button:not([disabled])')].filter(el => el.offsetParent !== null);

  // phone: bottom sheet; iPad and desktop: centred on the screen, wherever it was opened from
  const place = () => {
    const sheet = window.innerWidth < 761;
    cc.classList.toggle('kw-cc--sheet', sheet);
    cc.classList.toggle('kw-cc--center', !sheet);
    card.style.top = card.style.left = '';
  };

  const open = el => {
    clearTimeout(closeTimer);
    trigger = el || null;
    cc.hidden = false;
    document.documentElement.classList.add('kw-cc-open');
    place();
    void cc.offsetWidth; cc.classList.add('is-open');
    document.querySelectorAll('[data-contact-card]').forEach(t => t.setAttribute('aria-expanded', 'true'));
    const first = card.querySelector('.kw-cc-value--email') || card.querySelector('.kw-cc-x');
    setTimeout(() => first && first.focus({ preventScroll: true }), 60);
  };

  const close = () => {
    cc.classList.remove('is-open');
    document.documentElement.classList.remove('kw-cc-open');
    document.querySelectorAll('[data-contact-card]').forEach(t => t.setAttribute('aria-expanded', 'false'));
    closeTimer = setTimeout(() => { cc.hidden = true; status.textContent = ''; }, reduce.matches ? 0 : 380);
    if (trigger && trigger.offsetParent !== null) trigger.focus({ preventScroll: true });
  };

  document.querySelectorAll('[data-contact-card]').forEach(t => { t.setAttribute('aria-haspopup', 'dialog'); t.setAttribute('aria-expanded', 'false'); });
  document.addEventListener('click', e => {
    const t = e.target.closest('[data-contact-card]');
    if (!t) return;
    e.preventDefault();
    if (!cc.hidden && cc.classList.contains('is-open')) { close(); return; }
    open(t);
  }, true);

  cc.addEventListener('click', e => { if (e.target.closest('[data-cc-close]')) close(); });
  cc.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab') return;
    const f = focusables(); if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  window.addEventListener('resize', () => { if (!cc.hidden) place(); });

  /* copy */
  const copyText = async value => {
    try { await navigator.clipboard.writeText(value); return true; } catch (err) {
      const ta = document.createElement('textarea');
      ta.value = value; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta); ta.select();
      let ok = false; try { ok = document.execCommand('copy'); } catch (e2) {}
      ta.remove(); return ok;
    }
  };
  cc.querySelectorAll('[data-copy]').forEach(btn => {
    const label = btn.querySelector('span');
    const original = label ? label.textContent : '';
    btn.addEventListener('click', async () => {
      const ok = await copyText(btn.dataset.copy);
      if (!ok) return;
      btn.classList.add('is-done');
      if (label) label.textContent = cc.dataset.copied || 'Copied';
      status.textContent = (cc.dataset.copied || 'Copied') + ': ' + btn.dataset.copy;
      setTimeout(() => { btn.classList.remove('is-done'); if (label) label.textContent = original; }, 1800);
    });
  });

  /* save to contacts */
  const fold = line => {
    if (line.length <= 75) return line;
    const out = [line.slice(0, 75)];
    for (let i = 75; i < line.length; i += 74) out.push(' ' + line.slice(i, i + 74));
    return out.join('\r\n');
  };
  const toBase64 = blob => new Promise(res => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result).split(',')[1] || '');
    fr.onerror = () => res('');
    fr.readAsDataURL(blob);
  });
  const save = cc.querySelector('[data-cc-save]');
  if (save) save.addEventListener('click', async e => {
    e.preventDefault();
    let photo = '';
    try { const r = await fetch(cc.dataset.photo); if (r.ok) photo = await toBase64(await r.blob()); } catch (err) {}
    const lines = [
      'BEGIN:VCARD', 'VERSION:3.0', 'N:;;;;', 'FN:Kingdom Watch Company', 'ORG:Kingdom Watch Company',
      'EMAIL;TYPE=WORK,INTERNET:' + cc.dataset.email,
      'ADR;TYPE=WORK:;;;Cullman;AL;;United States', 'URL:' + cc.dataset.url, 'X-ABShowAs:COMPANY'
    ];
    if (photo) lines.push('PHOTO;ENCODING=b;TYPE=PNG:' + photo);
    lines.push('END:VCARD');
    const blob = new Blob([lines.map(fold).join('\r\n') + '\r\n'], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Kingdom Watch Company.vcf';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 8000);
    status.textContent = cc.dataset.saved || '';
  });
})();

/* Product description as a spec sheet. WatchTrack sends one block of text with section labels
   (Model Background, Specifications, Condition Report, Delivery Contents, Service & Authenticity,
   Shipping & Legal, Closing). When those labels are present the sections are arranged as a sheet:
   reading sections on the left, the specification ledger on the right, fine print at the foot.
   Nothing is reworded or removed; a listing without the labels is left exactly as written. */
(() => {
  const desc = document.querySelector('[data-watch-description]');
  if (!desc || desc.dataset.kwSheet) return;
  const KEYS = {
    'model background': 'overview', 'about this reference': 'overview', 'about the model': 'overview',
    'key features': 'overview', 'why this edition is special': 'overview', 'overview': 'overview',
    'specifications': 'specs', 'specs': 'specs', 'condition report': 'condition', 'condition': 'condition',
    'delivery contents': 'contents', "what's included": 'contents', 'included': 'contents', 'box and papers': 'contents',
    'service & authenticity': 'service', 'service and authenticity': 'service',
    'shipping & legal': 'legal', 'shipping and legal': 'legal', 'disclosure': 'legal', 'closing': 'closing'
  };
  const norm = t => t.replace(/\s+/g, ' ').trim().toLowerCase().replace(/[:\s]+$/, '');
  const keyOf = n => {
    if (n.nodeType !== 1 || !/^(H2|H3|H4|H5|P|STRONG|B|DIV)$/.test(n.tagName)) return null;
    const t = norm(n.textContent);
    return t && t.length <= 40 ? (KEYS[t] || null) : null;
  };
  const sections = [{ key: 'intro', head: null, nodes: [] }];
  [...desc.childNodes].forEach(n => {
    const k = keyOf(n);
    if (k) sections.push({ key: k, head: n, nodes: [] });
    else sections[sections.length - 1].nodes.push(n);
  });
  if (!sections.some(s => s.key === 'specs') || sections.length < 3) return;
  const before = desc.textContent.replace(/\s+/g, '');

  const KV = /^\s*([^:\n]{2,42}):\s*(\S[\s\S]*)$/;
  const fromLines = (html) => html.split(/<br\s*\/?\s*>/i).map(h => { const d = document.createElement('div'); d.innerHTML = h; return d.textContent; });
  // a paragraph of "Label: value" lines becomes a ledger; a paragraph of short plain lines becomes a list
  const restructure = (node, mode) => {
    if (node.nodeType !== 1 || node.tagName !== 'P' || node.querySelector('a,img,video,iframe')) return [node];
    const lines = fromLines(node.innerHTML).filter(l => l.trim());
    if (lines.length < 2) return [node];
    const kv = lines.filter(l => KV.test(l)).length;
    const out = [];
    if (kv >= Math.ceil(lines.length / 2)) {
      let dl = null;
      lines.forEach(l => {
        const m = l.match(KV);
        if (m) {
          if (!dl) { dl = document.createElement('dl'); dl.className = 'kw-sheet-ledger'; out.push(dl); }
          const g = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd');
          dt.textContent = m[1].trim(); const c = document.createElement('span'); c.className = 'kw-vh'; c.textContent = ':'; dt.append(c);
          dd.textContent = m[2].trim(); g.append(dt, dd); dl.append(g);
        } else { dl = null; const p = document.createElement('p'); p.textContent = l.trim(); out.push(p); }
      });
      return out;
    }
    if (mode === 'contents' && lines.every(l => l.trim().length < 90)) {
      const ul = document.createElement('ul'); ul.className = 'kw-sheet-list';
      lines.forEach(l => { const li = document.createElement('li'); li.textContent = l.trim(); ul.append(li); });
      return [ul];
    }
    return [node];
  };

  const sheet = document.createElement('div'); sheet.className = 'kw-sheet';
  const fine = document.createElement('div'); fine.className = 'kw-sheet-fine';
  sections.forEach((s, i) => {
    const sec = document.createElement('section');
    sec.className = 'kw-sheet-sec kw-sheet-sec--' + s.key;
    if (s.head) { s.head.classList.add('kw-sheet-h'); sec.append(s.head); }
    s.nodes.forEach(n => { restructure(n, s.key).forEach(x => sec.append(x)); });
    if (s.key === 'intro') {
      const first = [...sec.children].find(c => c.textContent.trim());
      if (first) first.classList.add('kw-sheet-lead');
    }
    if (!sec.textContent.trim()) return;
    (s.key === 'legal' || s.key === 'closing' ? fine : sheet).append(sec);
  });
  const frag = document.createDocumentFragment(); frag.append(sheet); if (fine.childNodes.length) frag.append(fine);
  const probe = document.createElement('div'); probe.append(frag.cloneNode(true));
  if (probe.textContent.replace(/\s+/g, '').replace(/:/g, '') !== before.replace(/:/g, '')) return; // never lose a word
  desc.replaceChildren(frag);
  // ledger labels read as labels: the trailing colon stays for screen readers only
  desc.querySelectorAll('.kw-sheet dt').forEach(dt => {
    const t = dt.lastChild;
    if (t && t.nodeType === 3 && /:\s*$/.test(t.nodeValue)) {
      t.nodeValue = t.nodeValue.replace(/:\s*$/, '').trimStart();
      const c = document.createElement('span'); c.className = 'kw-vh'; c.textContent = ':'; dt.append(c);
    }
  });
  desc.dataset.kwSheet = '1';
  desc.closest('.kw-fulldesc')?.classList.add('kw-fulldesc--sheet');
})();
